/**
 * FidelityRocker — trial ↔ run zoom toggle (4C).
 *
 * A controlled two-position control framed as a zoom level:
 *   trial = high detail (interleaved per-trial stream)
 *   run   = low detail (one-line-per-run rollup)
 *
 * Purely presentational: props in, JSX out. No fetch, no polling, no global
 * reads. The parent owns fidelity state and passes it down; this component emits
 * onChange and renders which segment is active. Default landing fidelity is trial
 * (the parent sets it — the component is controlled).
 *
 * Observatory Tabs underline pattern (design ref §6.4):
 *   - Flat corners, 1px border around the group
 *   - Active segment: var(--text-primary) + 2px bottom accent underline
 *   - Inactive: var(--text-secondary), hover → primary
 */

export type Fidelity = 'trial' | 'run'

export interface FidelityRockerProps {
  value: Fidelity
  onChange: (next: Fidelity) => void
}

/** ARIA Authoring Practices §Tabs pattern: arrow-key navigation between tabs. */
function handleRockerKeyDown(e: React.KeyboardEvent<HTMLDivElement>, onChange: (next: Fidelity) => void): void {
  if (e.key === 'ArrowRight') {
    e.preventDefault()
    onChange('run')
  } else if (e.key === 'ArrowLeft') {
    e.preventDefault()
    onChange('trial')
  }
}

export function FidelityRocker({ value, onChange }: FidelityRockerProps): JSX.Element {
  return (
    <div
      className="fidelity-rocker"
      role="tablist"
      aria-label="Fidelity zoom level"
      onKeyDown={(e) => handleRockerKeyDown(e, onChange)}
    >
      <button
        type="button"
        role="tab"
        className={`fidelity-rocker-tab${value === 'trial' ? ' is-active' : ''}`}
        aria-selected={value === 'trial'}
        tabIndex={value === 'trial' ? 0 : -1}
        data-testid="rocker-trial"
        onClick={() => onChange('trial')}
      >
        trial
      </button>
      <button
        type="button"
        role="tab"
        className={`fidelity-rocker-tab${value === 'run' ? ' is-active' : ''}`}
        aria-selected={value === 'run'}
        tabIndex={value === 'run' ? 0 : -1}
        data-testid="rocker-run"
        onClick={() => onChange('run')}
      >
        run
      </button>
    </div>
  )
}
